// src/utils/openHouseHostAccess.js
// Decision 10 of the 2026-09-16 open house design (Henry, restated 09/18): an OUTSIDE hosting agent (not
// one of our team agents) never gets a lockbox code or lockbox number, by any channel. They get the
// lockbox TYPE plus "The listing agent will share access details before the open house." A team host
// keeps today's line (Push Button with its code; Electronic is type only). A Seller Provided Door Code
// still goes to EVERY host and replaces the lockbox line (sellerDoorCode.js); nothing here touches it.
//
// This is the ONE browser gate: every hosting-agent email builder (scheduling, Re-send Invite, update,
// cancellation, text and HTML) runs the open house through forHostMessage before it reads a lockbox
// value, so a new caller cannot forget. The server twin is functions-api/ohRequestEmails.js lockboxLineFor.
//
// Pure and import-free, so openHouseEmails.js can import it without a cycle.

export const OUTSIDE_HOST_ACCESS_NOTE = "The listing agent will share access details before the open house.";

// Symbol keys, so a field stored on a Firestore doc can never pose as either one.
//   HOST_LOCKBOX_LINE: the lockbox line an outside host may see, attached by forHostMessage.
//   TEAM_COPY:         set by asTeamCopy on the listing agent's own copy of a host email.
export const HOST_LOCKBOX_LINE = Symbol("hostLockboxLine");
const TEAM_COPY = Symbol("teamCopy");

const str = (v) => String(v == null ? "" : v).trim();

// Is the host of this open house an outside agent? Fails safe: only a host with a team user id counts as
// team, because hostingAgentId is set only when the host was picked from, or resolved to, a CRM user:
//   - a request-made open house copies request.agentId, which is "" for an outside agent
//     (functions-api/ohRequests.js requestDocFrom, openHouseBuilder.js);
//   - ScheduleModal's "Outside agent" option writes hostingAgentId "";
//   - Up For Grabs and Quick Check-In always write the signed-in user's id.
// requestId is NOT part of the test: hand-scheduled outside hosts have none. The explicit
// hostIsTeamMember flag (written on new docs by the server builder, createOpenHouse, the Edit dialog and
// the kiosk) can only narrow toward outside: false means outside even with an id. A legacy doc with no
// hostingAgentId at all counts as outside, so it loses the code rather than leak it.
export function hostIsOutside(oh) {
  if (!oh || typeof oh !== "object") return true;
  if (oh.hostIsTeamMember === false) return true;
  return str(oh.hostingAgentId) === "";
}

// The lockbox line for a host. Word for word the server's lockboxLineFor(openHouse, includeCode).
export function hostLockboxLine(oh, includeCode) {
  const type = str(oh && oh.lockboxType);
  if (!type) return "";
  if (includeCode) {
    const code = str(oh.lockboxCode);
    return type === "Push Button" && code ? `${type} (code ${code})` : type;
  }
  return `${type}. ${OUTSIDE_HOST_ACCESS_NOTE}`;
}

// The open house as a host-facing message may see it. A team host (or the listing agent's own copy, see
// asTeamCopy) gets the open house back unchanged. An outside host gets a copy with lockboxNumber and
// lockboxCode blanked and the outside line under HOST_LOCKBOX_LINE, which lockboxSectionValue and the
// {{lockbox}} placeholder print. sellerDoorCode is left as is, so the door code still wins.
export function forHostMessage(oh) {
  if (oh && oh[TEAM_COPY] === true) return oh;
  if (!hostIsOutside(oh)) return oh;
  const base = oh && typeof oh === "object" ? oh : {};
  return {
    ...base,
    lockboxNumber: "",
    lockboxCode: "",
    [HOST_LOCKBOX_LINE]: hostLockboxLine(base, false),
  };
}

// The listing agent's copy of a hosting-agent email (openHouseSend.sendListingAgentCopy and the update and
// cancel copies). The listing agent is on the team and is the one who shares access with an outside
// host, so their copy keeps the full lockbox line.
export function asTeamCopy(oh) {
  return { ...(oh || {}), [TEAM_COPY]: true };
}
